// Libraries
import { InputAdornment, TextField } from "@material-ui/core";
import classNames from "classnames";
import { useObserver } from "mobx-react-lite";
import React, { ReactElement } from "react";
import ReactHtmlParser from "react-html-parser";

// Core
import { isEmptyOrWhitespace } from "../../../../Core/Utils/Utils";

// Custom
import { BQSEditNumberStyle } from "./../BQSPrimitivesStyle";
import { isNullOrUndefined } from "Custom/Utils/Utils";

interface IProps {
    className?: string;
    displayName: string;
    endAdornment?: string;
    execute: (value: number) => void;
    canExecute?: boolean;
    minimumValue?: number;
    maximumValue?: number;
    placeholder?: string;
    startAdornment?: string;
    stepValue?: number;
    value?: number;
    validationMessage?: string;
}

export const BQSEditNumber: React.FC<IProps> = (props: IProps) => {
    // #region Code Behind

    const classes = BQSEditNumberStyle();

    const getClassName = (): string => {
        return classNames({
            BQSEditNumber: true,
            [`${props.className}`]: !isEmptyOrWhitespace(props.className),
        });
    };

    const isEndAdornmentNotEmpty = (): boolean => {
        return !isEmptyOrWhitespace(props.endAdornment);
    };

    const isStartAdornmentNotEmpty = (): boolean => {
        return !isEmptyOrWhitespace(props.startAdornment);
    };

    const isDisabled = (): boolean => {
        return !isNullOrUndefined(props.canExecute) ? !props.canExecute : false;
    };

    const isValidationMessageNotEmpty = (): boolean => {
        return !isEmptyOrWhitespace(props.validationMessage);
    };

    const getDisplayName = (): ReactElement[] | string => {
        return !isEmptyOrWhitespace(props.displayName) ? ReactHtmlParser(props.displayName) : "";
    };

    const getPlaceholder = (): string => {
        return !isEmptyOrWhitespace(props.placeholder) ? props.placeholder! : "";
    };

    const getValidationMessage = (): string => {
        return !isEmptyOrWhitespace(props.validationMessage) ? props.validationMessage! : "";
    };

    const getValue = (): string => {
        return !isNullOrUndefined(props.value) ? props.value.toString() : "";
    };

    const onChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        props.execute(Number(event.target.value));
    };

    const onBlur = (): void => {
        const value = props.value;
        const min = props.minimumValue;
        const max = props.maximumValue;

        if (!isNullOrUndefined(value) && !isNullOrUndefined(min) && value < min) {
            props.execute(min);
        }

        if (!isNullOrUndefined(value) && !isNullOrUndefined(max) && value > max) {
            props.execute(max);
        }
    };

    const renderEndAdornment = (): React.ReactNode => {
        return isEndAdornmentNotEmpty() ? <InputAdornment position="end">{ReactHtmlParser(props.endAdornment!)}</InputAdornment> : <React.Fragment />;
    };

    const renderStartAdornment = (): React.ReactNode => {
        return isStartAdornmentNotEmpty() ? <InputAdornment position="start">{ReactHtmlParser(props.startAdornment!)}</InputAdornment> : <React.Fragment />;
    };

    // #endregion Code Behind

    // #region Render Result

    return useObserver(() => (
        <TextField
            classes={classes}
            className={getClassName()}
            disabled={isDisabled()}
            error={isValidationMessageNotEmpty()}
            helperText={getValidationMessage()}
            inputProps={{
                min: props.minimumValue,
                max: props.maximumValue,
            }}
            InputLabelProps={{
                shrink: true,
            }}
            InputProps={{
                disableUnderline: true,
                endAdornment: renderEndAdornment(),
                startAdornment: renderStartAdornment(),
            }}
            label={getDisplayName()}
            onBlur={onBlur}
            onChange={onChange}
            placeholder={getPlaceholder()}
            type="number"
            value={getValue()}
        />
    ));

    // #endregion Render Result
};
